import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

// Local components.
import * as S from './styled';

/**
 * Render a single button based on the given properties.
 *
 * @param {string} id The optional id to give to the button.
 * @param {string} className The optional class name to append to the button.
 * @param {string} type The type of button to render.
 * @param {boolean} primary If true then the `btn-primary` colors are used.
 * @param {boolean} alternate If true then the `btn-alt` colors are used.
 * @param {string|boolean} icon Optional icon to prepend before the label.
 * @param {string} title The tittle to give to the button.
 * @param {boolean} loading If true the button will have a loading state.
 * @param {boolean} disabled If true the button will have a disabled state.
 * @param {Function} onClick The method to fire when the button is clicked.
 * @param {string} size The size of the button, can be `small`, `medium`, or `large`.
 * @param {*} children The children to render as label in the button.
 *
 * @returns {Element} The rendered button.
 */
const Button = ({
  id = undefined,
  className = '',
  type = 'button',
  primary = false,
  alternate = false,
  icon = false,
  title = '',
  loading = false,
  disabled = false,
  onClick = false,
  size = 'medium',
  children = false,
}) => {
  const handleClick = useCallback(
    (e, ...props) => {
      e.stopPropagation();

      if (disabled === false && onClick !== false) {
        onClick(...props);
      }
    },
    [disabled, onClick],
  );

  return (
    <S.Button
      id={id}
      className={className}
      title={title}
      type={type}
      disabled={disabled}
      onClick={handleClick}
      // Styled props.
      primary={primary}
      alternate={alternate}
      size={size}
    >
      {loading && <S.Loader disabled={disabled} primary={primary} alternate={alternate} />}
      {!loading && icon && <S.Icon icon={icon} />}
      {children && <S.Label>{children}</S.Label>}
    </S.Button>
  );
};

Button.propTypes = {
  id: PropTypes.string,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  primary: PropTypes.bool,
  alternate: PropTypes.bool,
  icon: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  title: PropTypes.string,
  className: PropTypes.string,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  onClick: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  children: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string,
    PropTypes.element,
    PropTypes.arrayOf(PropTypes.element),
    PropTypes.node,
  ]),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
};

export default Button;
